import { Fragment, useMemo, useState, useEffect, useRef } from "react"
import withStyles from "react-jss"
import colors from "../../data/colors"
import {
  dimensions,
  getChartNodes,
  THRESHOLD_SCORE,
} from "../../utils/distributionChart"
import { ANIM_STATE } from "../../data/distribution-chart/annotations"
import { MOBILE_VIZ_TOP_PADDING } from "../../data/globalStyles"

const styles = {
  fullHeightContainer: {
    height: "100vh",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "sticky",
    top: 0,
    "@media only screen and (max-width: 840px)": {
      alignItems: "flex-start",
      position: "static",
      transform: "none",
    },
  },
  svg: {
    width: "100%",
    "@media only screen and (max-width: 840px)": {
      marginTop: MOBILE_VIZ_TOP_PADDING,
    },
  },
  text: {
    fontFamily: "WiredMono",
    fill: "#ffffff",
  },
  anim: {
    transitionProperty: "opacity",
    transitionTimingProperty: "ease-in", // duration given inline
    willChange: "opacity",
  },
  line: {
    composes: "$anim",
    pointerEvents: "none",
    transitionDuration: "0.3s",
  },
}

const DotPlot = ({ classes, state }) => {
  const {
    width,
    height,
    radius,
    padding: { left: pLeft, right: pRight, bottom: pBottom, top: pTop },
  } = dimensions
  const {
    SCORE_EXTENT,
    xScale,
    nodesOverall,
    legendParenthood,
    legendLanguage,
    nodesLanguagePass,
    nodesLanguageFail,
    nodesParenthoodPass,
    nodesParenthoodFail,
  } = useMemo(() => getChartNodes(), [])

  const CHARTS = {
    none: "none",
    overall: "DISTRIBUTION OF RISK SCORES",
    parenthood: "PARENTHOOD",
    language: "LANGUAGE",
  }
  // Using ref so that the animation step is
  // accessible through setTimeout()
  const step = useRef(0)
  const [data, setData] = useState({
    activeChart: CHARTS.none,
    leftTop: 0,
    rightTop: 0,
    leftFull: 1,
    rightFull: 1,
    topDuration: 0, // seconds
    fullDuration: 0, // seconds
  })

  const resetChart = () => {
    step.current = 0
    setData({
      activeChart: CHARTS.overall,
      leftTop: 0,
      rightTop: 0,
      leftFull: 1,
      rightFull: 1,
      topDuration: 0.3,
      fullDuration: 0,
    })
  }
  const overall = () => {
    step.current = 1
    setData({
      activeChart: CHARTS.overall,
      leftTop: 0,
      rightTop: 0,
      leftFull: 0,
      rightFull: 0,
      topDuration: 0.3,
      fullDuration: 0.3,
    })
  }
  const langReq = () => {
    step.current = 2
    setData({
      activeChart: CHARTS.language,
      leftTop: 0,
      rightTop: 0.75,
      leftFull: 0.75,
      rightFull: 0,
      topDuration: 0.3,
      fullDuration: 0.3,
    })
  }
  const parents1 = () => {
    step.current = 3
    setData({
      activeChart: CHARTS.language,
      leftTop: 1,
      rightTop: 1,
      leftFull: 1,
      rightFull: 1,
      topDuration: 0.3,
      fullDuration: 0.3,
    })
    setTimeout(() => {
      if (step.current === 3) {
        setData({
          activeChart: CHARTS.parenthood,
          leftTop: 0,
          rightTop: 0.75,
          leftFull: 0.75,
          rightFull: 0,
          topDuration: 0.3,
          fullDuration: 0.3,
        })
      }
    }, 600)
  }

  useEffect(() => {
    const mapping = {
      "": resetChart,
      [ANIM_STATE[0]]: overall,
      [ANIM_STATE[1]]: langReq,
      [ANIM_STATE[2]]: parents1,
    }
    mapping[state]?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  return (
    <div className={classes.fullHeightContainer}>
      <svg
        className={classes.svg}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        xmlns="http://www.w3.org/2000/svg"
        role="img">
        <title>
          Chart showing distribution of scores for a random selection of
          thousand welfare recipients based on the recipient's parenthood status
          and language requirements.
        </title>
        {/* Pass nodes */}
        <g id="distribution-nodes1">
          {data.activeChart === CHARTS.overall &&
            nodesOverall.map((d) => (
              <circle
                key={d.id}
                cx={d.cx}
                cy={d.cy}
                fill={d.fill}
                strokeWidth={d.strokeWidth}
                stroke={d.stroke}
                r={radius}
              />
            ))}
          {data.activeChart === CHARTS.language &&
            nodesLanguagePass.map((d) => (
              <circle
                key={d.id}
                cx={d.cx}
                cy={d.cy}
                fill={d.fill}
                strokeWidth={d.strokeWidth}
                stroke={d.stroke}
                r={radius}
              />
            ))}
          {data.activeChart === CHARTS.parenthood &&
            nodesParenthoodPass.map((d) => (
              <circle
                key={d.id}
                cx={d.cx}
                cy={d.cy}
                fill={d.fill}
                strokeWidth={d.strokeWidth}
                stroke={d.stroke}
                r={radius}
              />
            ))}
          {/* 0 to threshold line */}
          <rect
            className={classes.anim}
            x={xScale(SCORE_EXTENT[0])}
            y={pTop + 60}
            width={xScale(THRESHOLD_SCORE) - xScale(SCORE_EXTENT[0]) + 1}
            height={height - pBottom - (pTop + 60)}
            fill="#000000"
            opacity={data.leftTop}
            style={{
              transitionDuration: `${data.topDuration}s`,
            }}
          />
          {/* threshold line to MAX */}
          <rect
            className={classes.anim}
            x={xScale(THRESHOLD_SCORE)}
            y={pTop + 60}
            width={xScale(SCORE_EXTENT[1]) - xScale(THRESHOLD_SCORE) + 1}
            height={height - pBottom - (pTop + 60)}
            fill="#000000"
            opacity={data.rightTop}
            style={{
              transitionDuration: `${data.topDuration}s`,
            }}
          />
        </g>
        {/* Fail nodes */}
        <g id="distribution-nodes2">
          {data.activeChart === CHARTS.language &&
            nodesLanguageFail.map((d) => (
              <circle
                key={d.id}
                cx={d.cx}
                cy={d.cy}
                fill={d.fill}
                strokeWidth={d.strokeWidth}
                stroke={d.stroke}
                r={radius}
              />
            ))}
          {data.activeChart === CHARTS.parenthood &&
            nodesParenthoodFail.map((d) => (
              <circle
                key={d.id}
                cx={d.cx}
                cy={d.cy}
                fill={d.fill}
                strokeWidth={d.strokeWidth}
                stroke={d.stroke}
                r={radius}
              />
            ))}
          {/* 0 to threshold line */}
          <rect
            className={classes.anim}
            x={xScale(SCORE_EXTENT[0])}
            y={pTop + 60}
            width={xScale(THRESHOLD_SCORE) - xScale(SCORE_EXTENT[0]) + 1}
            height={height - pBottom - (pTop + 60)}
            fill="#000000"
            opacity={data.leftFull}
            style={{
              transitionDuration: `${data.fullDuration}s`,
            }}
          />
          {/* threshold line to MAX */}
          <rect
            className={classes.anim}
            x={xScale(THRESHOLD_SCORE)}
            y={pTop + 60}
            width={xScale(SCORE_EXTENT[1]) - xScale(THRESHOLD_SCORE) + 1}
            height={height - pBottom - (pTop + 60)}
            fill="#000000"
            opacity={data.rightFull}
            style={{
              transitionDuration: `${data.fullDuration}s`,
            }}
          />
        </g>
        {/* Chart base */}
        <g id="distribution-chart-base">
          {data.activeChart !== CHARTS.none && (
            <text
              className={classes.text}
              x={pLeft}
              y={pTop + 40}
              fontSize="20px"
              dominantBaseline="middle">
              {data.activeChart}
            </text>
          )}
          <line
            x1={pLeft}
            y1={height - pBottom}
            x2={width - pRight}
            y2={height - pBottom}
            stroke="white"
          />
          {/* Red vertical line */}
          <line
            className={classes.line}
            x1={xScale(THRESHOLD_SCORE) + 0.5}
            y1={pTop + 60}
            x2={xScale(THRESHOLD_SCORE) + 0.5}
            y2={height - pBottom - 1}
            stroke={colors.redLine}
            strokeWidth="2"
            style={{ opacity: data.activeChart !== CHARTS.none ? 1 : 0 }}
          />
          <text
            className={classes.text}
            x={pLeft}
            y={height - pBottom + 15}
            textAnchor="start"
            dominantBaseline="hanging">
            LOW RISK
          </text>
          <text
            className={classes.text}
            x={width - pRight}
            y={height - pBottom + 15}
            textAnchor="end"
            dominantBaseline="hanging">
            HIGH RISK
          </text>
        </g>
        {/* Legend - PARENTHOOD */}
        <g
          id="distribution-parenthood-legend"
          className={classes.anim}
          transform={`translate(0, ${pTop + 40})`}
          style={{ opacity: data.activeChart === CHARTS.parenthood ? 1 : 0 }}>
          {legendParenthood.map((d, i) => (
            <Fragment key={i}>
              <text
                x={d.textX}
                y={d.textY}
                fill={colors.white}
                textAnchor="end"
                fontFamily="WiredMono"
                fontSize="12px"
                dominantBaseline="middle">
                {d.name}
              </text>
              <circle
                r={d.r}
                cx={d.cx}
                cy={d.cy}
                fill={d.fill}
                stroke={d.stroke}
                strokeWidth={d.strokeWidth}
              />
            </Fragment>
          ))}
        </g>
        {/* Legend - LANGUAGE */}
        <g
          id="distribution-language-legend"
          className={classes.anim}
          transform={`translate(0, ${pTop + 40})`}
          style={{ opacity: data.activeChart === CHARTS.language ? 1 : 0 }}>
          {legendLanguage.map((d, i) => (
            <Fragment key={i}>
              <text
                x={d.textX}
                y={d.textY}
                fill={colors.white}
                textAnchor="end"
                fontFamily="WiredMono"
                fontSize="12px"
                dominantBaseline="middle">
                {d.name}
              </text>
              <circle
                r={d.r}
                cx={d.cx}
                cy={d.cy}
                fill={d.fill}
                stroke={d.stroke}
                strokeWidth={d.strokeWidth}
              />
            </Fragment>
          ))}
        </g>
      </svg>
    </div>
  )
}

export default withStyles(styles)(DotPlot)
